import { useCallback, useEffect, useState, useRef } from 'react'
import { apiUrl } from 'constants/api';
import BookCard from 'components/BookCard';
import Input from 'components/common/Input';

import { GiMagnifyingGlass } from 'react-icons/gi';
import { FaSadCry } from 'react-icons/fa';
import Spinner from 'components/common/Spinner';
import debounce from 'utils/debounce';
import { toast } from 'react-toastify';

const searchCache = {}

const SearchBooks = () => {
    // refs
    const termRef = useRef('');

    const [searchTerm, setTerm] = useState('');
    const [results, setResults] = useState([])
    const [page, setPage] = useState(1);
    const [loading, setLoading] = useState(false);
    const [resultsTotal, setResultsTotal] = useState(0);

    const fetchBooks = useCallback(debounce(async (e, currTerm, currPage) => {
        try {
            setLoading(true);

            const newSearchTerm = e?.target ? e.target.value : currTerm;

            if(newSearchTerm === '') {
                setResults([]);
                setTerm('');
                setResultsTotal(0)
    
                return;
            } else {
                const isTermSame = termRef.current === currTerm;
                const newPage = isTermSame ? currPage + 1 : 1;

                const url = `${apiUrl}/search.json?` + new URLSearchParams({
                    title: newSearchTerm ?? currTerm,
                    page: newPage,
                    limit: 12
                });

                let booksData = null;

                // Check for cache hit
                if(searchCache[url]) {
                    booksData = searchCache[url];
                } else {
                    const response = await fetch(url);
    
                    const data = await response.json();

                    searchCache[url] = data.docs;

                    booksData = data.docs;

                    // Update results total state
                    setResultsTotal(data.numFound);
                }
    
                setPage(newPage)

                setResults(c => {
                    termRef.current = newSearchTerm;
    
                    if(isTermSame) {
                        // Append data
                        return [...c, ...booksData]
                    } else {
                        // Return new
                        return booksData
                    }
                })
            }
        } catch (error) {
            console.error(error);

            toast.error('An error occured while fetching books...');
        } finally {
            setLoading(false);
        }
    }, 600), [setTerm, setResults, setLoading, setPage]);

    const handleInputChange = (e) => {
        fetchBooks(e, searchTerm, page);

        setTerm(e.target.value)
    }

    useEffect(() => {
        const scrollHandler = () => {
            // Figure out if there are more books to fetch
            if(page >= resultsTotal / 12) return;

            if ((window.innerHeight + window.scrollY) >= document.body.offsetHeight) {
                fetchBooks(false, searchTerm, page);
            }
        }

        window.addEventListener('scroll', scrollHandler);

        return () => window.removeEventListener('scroll', scrollHandler);
    }, [fetchBooks, searchTerm, page, resultsTotal])

    return (
        <div>
            <div className="my-5">
                <Input type="text" value={searchTerm} loading={loading} disabled={loading} onChange={handleInputChange} placeholder="Search by book title..." />
                <p className="mt-2">Scroll to the bottom of the page to trigger infinite scroll</p>
            </div>

            <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5 relative">

                {
                    // TODO if time, optimize rerenders on fetch, virtualization ...
                    results.map(({ title, cover_i, key, isbn }, idx) => (
                        <BookCard key={`${key}-${idx}`} roundedCover data={{ title, cover_i, isbn }} />
                    ))
                }

                { 
                    loading && (
                        <div className="absolute left-0 top-0 w-full h-full bg-white/50 z-20">
                            <Spinner className="sticky top-1/2 left-1/2 -translate-x-1/2" />
                        </div>
                    )
                }
            </div>

            <div className="no-results">
            {
                loading ? (
                    <h2>Looking for books...</h2>
                ) : (
                    (results.length === 0 && !loading) && (
                        searchTerm !== '' ? (
                            <p className="flex">
                                <FaSadCry className="mr-2 mt-1" />
                                No books found
                            </p>
                        ) : (
                            <p className="flex">
                                <GiMagnifyingGlass className="mr-2 mt-1" />
                                Search for books using the input above
                            </p>
                        )
                    )
                )
            }
            </div>
        </div>
    )
}

export default SearchBooks